/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { SEText } from "../../../../components/SEText";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { AgentesSSEInterface } from "../../../../interfaces";
import { AgentesSSEService } from "../services";
import useRenderFormStore from "../../stores/useRenderFormStore";
import {
  capitalizeWords,
  onlyNumbers,
  removeEmpty,
} from "../../../../../utils/utils";
import { BaSeDialogContext } from "@base/react";
import useFormMutation from "../../hooks/useFormMutation";
import theme from "../../../../mui-theme";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router";
import { TextFieldCounter } from "../../../../components/TextFieldCounter";
import { SEFormUserAction } from "../../../../components/SEFormUserAction";
import { RiCloseCircleLine, RiSearchLine } from "react-icons/ri";
import SEFilterSelect from "../../../../components/SEFilterSelect";
import { BsPlusCircle } from "react-icons/bs";
import { cpfMask, retirarMascara } from "../../../../../utils/mask";
import { useQuery } from "react-query";
import { parseStringToJson, sortArrayByProp } from "../../../../utils/utils";
import useFilterHandler from "../../../../hooks/useFilterHandler";
import { handleModalRedirect } from "../../../../../utils/handleModalRedirect";
import { handleModalStatus } from "../../../../../utils/handleModalStatus";

const CustomIcon = styled(RiSearchLine)(({ theme }) => ({
  fill: theme.palette.primary.main,
  height: 26,
  width: 26,
  top: "calc(50% - .75em) !important",
}));

interface FilterOptions {
  tipo: { value: string; label: string }[];
  uf: { sigla: string; codSebrae: string }[];
}

interface CPFResult {
  cpf: string;
  nomeRazaoSocial: string;
  codParceiro: string;
  codSebrae: string;
  listaVinculo: { nomeRazaoSocialPJ: string; descCargCli: string }[];
}

const FormularioAgentesSSE = () => {
  const { showModal } = React.useContext(BaSeDialogContext);
  const history = useHistory();
  const location = useLocation();
  const hasCopyParam = location.pathname.includes("/copy");

  const { data: filterOptions } = useQuery<FilterOptions>({
    queryFn: () => AgentesSSEService.getOptions(false),
    queryKey: ["getOptionsAgentesSSE"],
    refetchOnMount: false,
  });

  const {
    formData: editFormData,
    setFormData: setEditFormData,
    handleShowForm,
  } = useRenderFormStore();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<AgentesSSEInterface>({
    defaultValues: {
      ufs: [],
      cpf: "",
      nome: "",
      codRegistroSas: "",
      treinamento: false,
    },
    criteriaMode: "all",
  });

  const [cpfValidated, setCpfValidated] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [defaultFirstValue, setDefaultFirstValue] = useState("");
  const [searchCPFResult, setSearchCPFResult] = useState<CPFResult | null>(
    null
  );
  const [searchUFResult, setSearchUFResult] = useState(null);
  const { paginationFilters } = useFilterHandler();

  const onResetFields = () => {
    reset({
      cpf: "",
      nome: "",
      codRegistroSas: "",
      tipo: "",
      treinamento: false,
      uf: "",
      codUnidadeOrganizacional: null,
      descricaoUnidadeOrganizacional: "",
      empresaVinculada: "",
    });
    setCpfValidated(false);
  };

  const {
    fields: ufsFields,
    append: appendUf,
    remove: removeUf,
  } = useFieldArray({
    control,
    name: "ufs",
    rules: {
      required:
        "O registro não pôde ser criado porque ainda há campos que não foram preenchidos.",
    },
  });

  const selectedInfo = watch("ufs");

  const isAlreadyAdded = useMemo(() => {
    const formValues = getValues();
    return selectedInfo?.some(
      (field: any) =>
        JSON.stringify(field.uf) === formValues.uf &&
        field.descricaoUnidadeOrganizacional ===
          formValues.descricaoUnidadeOrganizacional &&
        field.codUnidadeOrganizacional ===
          formValues.codUnidadeOrganizacional &&
        field.empresaVinculada === formValues.empresaVinculada
    );
  }, [selectedInfo, getValues]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "uf") {
        setUnidades([]);
        setValue("descricaoUnidadeOrganizacional", "");
        setValue("codUnidadeOrganizacional", null);
        clearErrors([
          "codUnidadeOrganizacional",
          "descricaoUnidadeOrganizacional",
        ]);
      }

      if (name === "cpf") {
        const cpf = watch("cpf");
        const editCPF = cpfMask(editFormData?.cpf);
        if (cpf.length === 14 && editCPF !== cpf) {
          setTimeout(() => {
            refetchFindCPF();
          }, 100);
        }
      }
      if (name === "descricaoUnidadeOrganizacional") {
        const descricaoUnidadeOrganizacionalParsed = parseStringToJson(
          // @ts-ignore
          value[name]
        );
        if (descricaoUnidadeOrganizacionalParsed?.codigoUnidadeOrganizacional) {
          setValue(
            "codUnidadeOrganizacional",
            descricaoUnidadeOrganizacionalParsed?.codigoUnidadeOrganizacional
          );
          clearErrors("codUnidadeOrganizacional");
        }
      }
      return value;
    });
    return () => subscription.unsubscribe();
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const handleListaVinculo = (
    lista: { nomeRazaoSocialPJ: string; descCargCli: string }[]
  ) => {
    return lista
      .map((l) => ({
        nomeRazaoSocialPJ: l.nomeRazaoSocialPJ,
        descCargCli: l?.descCargCli,
      }))
      .filter((i) => i.nomeRazaoSocialPJ);
  };

  useEffect(() => {
    async function loadedData() {
      if (editFormData?.id) {
        reset(editFormData);
        setValue("empresaVinculada", "");
        setValue("uf", "");
        setValue("descricaoUnidadeOrganizacional", "");
        setValue("codUnidadeOrganizacional", null);
        setValue("cpf", cpfMask(editFormData.cpf));
        if (editFormData.cpf) {
          refetchFindCPF();
        }
      }
    }

    loadedData();
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFormData]);

  const goBack = () => {
    handleShowForm(false);
    history.push(`/tabelas-de-controle/agentes-consultores`);
  };

  const handleEditClick = async (item: AgentesSSEInterface) => {
    const body = {
      cpf: retirarMascara(item.cpf),
    };

    const response = await AgentesSSEService.findAll(paginationFilters, body);
    if (!response.content) return;
    const agente = response.content?.find((a: AgentesSSEInterface) => {
      return retirarMascara(a.cpf) === retirarMascara(item.cpf);
    });
    if (!!agente) {
      setEditFormData(agente);
    }
  };

  const handleExists = (item: AgentesSSEInterface) => {
    handleModalRedirect({
      title: "Agente SSE",
      content: `Já existe um agente SSE cadastrado com o CPF: ${item.cpf}, deseja edita-lo?`,
      onFinish: () => handleEditClick(item),
      onCancelClick: () => console.log("modal closed..."),
      // @ts-ignore
      showModal,
    });
  };

  const getUnidades = async () => {
    const uf = parseStringToJson(watch("uf")) || null;
    const codUnidadeOrganizacional = watch("codUnidadeOrganizacional") || null;
    clearErrors(["codUnidadeOrganizacional", "descricaoUnidadeOrganizacional"]);
    if (!uf) {
      setError("codUnidadeOrganizacional", { message: "Selecione uma UF" });
      return;
    }
    try {
      const response = await AgentesSSEService.getUnidades(
        uf.codSebrae,
        codUnidadeOrganizacional
      );

      setUnidades([]);
      if (response) {
        setDefaultFirstValue("Selecione uma unidade");
        if (codUnidadeOrganizacional) {
          const unidadeFiltered = response.find(
            (r: any) =>
              r?.codigoUnidadeOrganizacional ===
              // @ts-ignore
              parseInt(codUnidadeOrganizacional)
          );
          setValue("descricaoUnidadeOrganizacional", unidadeFiltered);
        }
        setUnidades(response);
      } else {
        setDefaultFirstValue("Nenhuma unidade encontrada");
      }
      return response;
    } catch (e) {
      setDefaultFirstValue("Nenhuma unidade encontrada");
      setError("descricaoUnidadeOrganizacional", {
        message: `Falha ao tentar localizar as unidades`,
      });
    }
  };

  const { createMutation, updateMutation } = useFormMutation({
    showModal,
    service: AgentesSSEService,
    onCloseModalSuccess: (res) => {
      onResetFields();
      goBack();
    },
    onCloseModalError: (error) => {
      const hasErrorMessage = error?.response?.data?.message;
      if (
        hasErrorMessage?.includes(
          "Já existe um atendente nan cadastrado com o cpf"
        )
      ) {
        return handleExists(getValues());
      }
      handleModalStatus({
        type: "error",
        msgOnError:
          hasErrorMessage ??
          `O agente SSE não foi ${
            editFormData?.id ? "editado" : "criado"
          }. Aconteceu um erro ou o sistema está temporariamente fora do ar. Tente novamente mais tarde.`,
        // @ts-ignore
        showModal,
      });
    },
    onCloseModalUpdate: () => goBack(),
  });

  const submitForm = async (dataForm: AgentesSSEInterface) => {
    if (hasCopyParam) {
      delete dataForm.id;
    }
    if (dataForm.uf) {
      dataForm.uf = parseStringToJson(dataForm.uf);
    }

    dataForm.ufs = dataForm.ufs.map((i) => {
      i.tipo = dataForm.tipo;
      return i;
    });

    dataForm = removeEmpty(dataForm);
    dataForm.cpf = onlyNumbers(dataForm.cpf);

    if (dataForm.id) {
      await updateMutation(removeEmpty(dataForm)).then();
    } else {
      await createMutation(removeEmpty(dataForm));
    }
  };

  const handleUFSearch = async (codSebrae: string) => {
    await AgentesSSEService.findUF(codSebrae)
      .then((response) => {
        setSearchUFResult(response);
      })
      .catch((err) => {
        console.log("error", err);
      });
  };

  const cpf = watch("cpf");

  const {
    data: findCPF,
    refetch: refetchFindCPF,
    isLoading: isLoadingCPF,
  } = useQuery<CPFResult>({
    queryFn: () =>
      AgentesSSEService.findCPF(retirarMascara(cpf), editFormData?.cpf),
    queryKey: ["findCPFAgentesConsultores"],
    enabled: cpf.length === 11,
    onSuccess: (response) => {
      if (!!response) {
        setSearchCPFResult(response);
        handleUFSearch(response.codSebrae);
        clearErrors();
        setCpfValidated(true);
      } else {
        setSearchCPFResult(null);
        setError("cpf", { message: "CPF não encontrado na base" });
      }
    },
  });

  useEffect(() => {
    if (searchCPFResult) {
      if (searchCPFResult.nomeRazaoSocial) {
        setValue("nome", capitalizeWords(searchCPFResult.nomeRazaoSocial));
      }
      if (searchCPFResult.codParceiro) {
        setValue("codRegistroSas", searchCPFResult.codParceiro);
      }
    }
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCPFResult]);

  useEffect(() => {
    if (searchUFResult) {
      setValue("uf", searchUFResult);
    }
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUFResult]);

  const handleAddNewUf = () => {
    const {
      uf,
      descricaoUnidadeOrganizacional,
      codUnidadeOrganizacional,
      empresaVinculada,
      tipo,
    } = getValues();

    const requiredFields = ["uf", "codUnidadeOrganizacional"];
    // @ts-ignore
    if (requiredFields.some((field) => !getValues()[field])) {
      setError("ufs", { message: "Selecione os campos obrigatórios" });
      return;
    }

    const formValues = {
      uf: parseStringToJson(uf),
      descricaoUnidadeOrganizacional: parseStringToJson(
        descricaoUnidadeOrganizacional
      )?.descricao,
      codUnidadeOrganizacional,
      empresaVinculada,
      tipo,
    };

    const formValuesCopy = { ...formValues };
    appendUf(formValuesCopy);
    clearErrors("ufs");
    setValue("uf", "");
    setValue("descricaoUnidadeOrganizacional", "");
    setValue("codUnidadeOrganizacional", null);
    setValue("empresaVinculada", "");
  };

  return (
    <ThemeProvider theme={theme}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 2,
        }}
      >
        <Grid container>
          <Grid container>
            <Grid item xs={12}>
              <SEText
                as="h2"
                size={"medium"}
                font="Mulish"
                weight="bold"
                color="#7B80FF"
              >
                {editFormData?.id
                  ? hasCopyParam
                    ? "Copiar"
                    : "Editar"
                  : "Incluir"}
              </SEText>
            </Grid>
          </Grid>

          <Grid container marginTop={3} spacing={2}>
            <Grid item md={3}>
              <Controller
                control={control}
                name="cpf"
                rules={{ required: "Este campo é obrigatório" }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <>
                    <TextField
                      id="cpf"
                      label="CPF *"
                      placeholder="Digite um CPF válido"
                      fullWidth
                      value={cpfMask(value ?? "")}
                      onChange={onChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {/* @ts-ignore */}
                            <IconButton
                              disabled={cpf.length < 11}
                              aria-label="search"
                              onClick={refetchFindCPF}
                            >
                              <RiSearchLine />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {error && (
                      <p style={{ color: "red", marginTop: "8px" }}>
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        {isLoadingCPF && <p>Carregando...</p>}

        {cpfValidated && !isLoadingCPF && (
          <Grid container spacing={2} marginTop={2} alignItems={"center"}>
            <Grid item md={3}>
              <Controller
                control={control}
                name="nome"
                rules={{ required: "Este campo é obrigatório" }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <>
                    <TextField
                      id="nome"
                      label="Nome *"
                      placeholder="Nome do agente"
                      fullWidth
                      value={value ?? ""}
                      onChange={onChange}
                      inputProps={{ maxLength: 140 }}
                    />
                    <TextFieldCounter value={value} maxLength={140} />
                    {error && (
                      <p style={{ color: "red", marginTop: "8px" }}>
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </Grid>

            <Grid item md={3}>
              <Controller
                control={control}
                name="codRegistroSas"
                rules={{ required: "Este campo é obrigatório" }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <>
                    <TextField
                      id="codRegistroSas"
                      label="Cód. Parceiro *"
                      placeholder="Digite o código"
                      fullWidth
                      value={value ?? ""}
                      onChange={onChange}
                      inputProps={{ maxLength: 140 }}
                    />
                    <TextFieldCounter value={value} maxLength={140} />
                    {error && (
                      <p style={{ color: "red", marginTop: "8px" }}>
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </Grid>

            <Grid item md={3} paddingBottom={1.75}>
              <Controller
                control={control}
                name="tipo"
                rules={{ required: "Este campo é obrigatório" }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <SEFilterSelect
                    id="tipo"
                    label="Perfil *"
                    defaultOption={"Selecione uma das opções"}
                    IconComponent={CustomIcon}
                    value={value}
                    onChange={onChange}
                    // @ts-ignore
                    options={filterOptions?.tipo}
                    error={error}
                  />
                )}
              />
            </Grid>

            <Grid item md={3} paddingBottom={1.75}>
              <Controller
                name="treinamento"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl component="fieldset" error={Boolean(error)}>
                    <FormLabel component="legend" sx={{ fontSize: 16 }}>
                      Testador Beta *
                    </FormLabel>
                    <RadioGroup row {...field}>
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="Não"
                      />
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Sim"
                      />
                    </RadioGroup>
                    {error && (
                      <p style={{ color: "red", marginTop: "8px" }}>
                        {error.message}
                      </p>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid container item spacing={3} alignItems="center">
              <Grid item md={1}>
                <Controller
                  control={control}
                  name="uf"
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <SEFilterSelect
                      id={"uf"}
                      defaultOption={"Selecione uma das opções"}
                      IconComponent={CustomIcon}
                      label="UF *"
                      value={value}
                      onChange={onChange}
                      options={sortArrayByProp(filterOptions?.uf, "sigla")}
                      displayField="sigla"
                      error={error}
                    />
                  )}
                />
              </Grid>

              <Grid item md={2}>
                <Controller
                  control={control}
                  name="codUnidadeOrganizacional"
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <>
                      <TextField
                        id="codUnidadeOrganizacional"
                        label="Cód. Unid. Org. *"
                        placeholder="Digite um código válido"
                        fullWidth
                        value={value}
                        onChange={onChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="search"
                                onClick={getUnidades}
                              >
                                <RiSearchLine />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {error && (
                        <p style={{ color: "red", marginTop: "8px" }}>
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </Grid>

              <Grid item md={3}>
                <Controller
                  control={control}
                  name="descricaoUnidadeOrganizacional"
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <>
                      <SEFilterSelect
                        id="descricaoUnidadeOrganizacional"
                        label="Descrição Unid. Org. *"
                        placeholder="Nome da agência"
                        defaultOption={defaultFirstValue}
                        IconComponent={CustomIcon}
                        value={value}
                        onChange={onChange}
                        options={unidades}
                        displayField="descricao"
                        error={error}
                      />
                      {error && (
                        <p style={{ color: "red", marginTop: "8px" }}>
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </Grid>

              <Grid item md={5}>
                <Controller
                  control={control}
                  name="empresaVinculada"
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <SEFilterSelect
                      id={"empresaVinculada"}
                      defaultOption={"Selecione uma das opções"}
                      IconComponent={CustomIcon}
                      label="Empresa habilitada *"
                      value={value}
                      onChange={onChange}
                      // @ts-ignore
                      options={handleListaVinculo(findCPF?.listaVinculo)}
                      displayField="nomeRazaoSocialPJ"
                      valueField="nomeRazaoSocialPJ"
                      error={error}
                    />
                  )}
                />
              </Grid>

              <Grid item md={1}>
                <IconButton
                  onClick={handleAddNewUf}
                  type="button"
                  disabled={isAlreadyAdded}
                >
                  <BsPlusCircle
                    size={30}
                    color={isAlreadyAdded ? "#9ca2a8" : "#0061C6"}
                  />
                </IconButton>
              </Grid>
              <Grid container item py={2}>
                {!!(errors?.ufs ?? errors?.ufs?.root) && (
                  <p style={{ color: "#F5365C" }}>
                    {/* @ts-ignore */}
                    {errors?.ufs?.message ?? errors?.ufs?.root?.message}
                  </p>
                )}
              </Grid>
            </Grid>

            {ufsFields.length > 0 && (
              <Grid container spacing={2} marginTop={2} marginLeft={1}>
                <Box
                  display="flex"
                  flexDirection={"column"}
                  gap="0.5rem"
                  flexWrap="wrap"
                  mt={3}
                  width="80%"
                >
                  {ufsFields.map((field, index) => {
                    return (
                      <Box
                        key={index}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            backgroundColor: "#9999FF1A",
                            padding: "8px",
                            borderRadius: "8px",
                            border: "1px solid #E1E6EF",
                            width: "100%",
                          }}
                        >
                          <SEText
                            as="h2"
                            size={"medium"}
                            font="Mulish"
                            weight="bold"
                          >
                            {/* @ts-ignore */}
                            {field?.uf?.sigla} - {/* @ts-ignore */}
                            {field?.codUnidadeOrganizacional} -{" "}
                            {/* @ts-ignore */}
                            {field?.descricaoUnidadeOrganizacional}
                            {/* @ts-ignore */}
                            {!!field?.empresaVinculada &&
                              /* @ts-ignore */
                              `- ${field?.empresaVinculada}`}
                          </SEText>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ marginLeft: 2, borderColor: "#E0E0E0" }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => removeUf(index)}
                          >
                            <RiCloseCircleLine size={20} color={"#FF0000"} />
                          </IconButton>
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            )}

            <Grid
              item
              md={12}
              display="flex"
              justifyContent="flex-end"
              gap={2}
              marginTop={2}
            >
              <Button variant="outlined" onClick={goBack}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit(submitForm)}
                disabled={false}
              >
                {editFormData?.id
                  ? hasCopyParam
                    ? "Copiar"
                    : "Editar"
                  : "Incluir"}
              </Button>
            </Grid>

            <Grid item md={12} marginTop={3}>
              <SEFormUserAction data={editFormData} />
            </Grid>
          </Grid>
        )}
      </Paper>
    </ThemeProvider>
  );
};

export default FormularioAgentesSSE;
